import { useState } from "react";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "@/i18n";

/**
 * One-click .xlsx download for any /reports/{kind}/xlsx-style endpoint.
 * Additive sibling of DownloadPdfButton — does not replace or alter it.
 */
export default function DownloadXlsxButton({
    endpoint, params = {}, filename, label = "Excel",
    className = "h-10 px-4 inline-flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 text-sm font-semibold hover:bg-emerald-100 disabled:opacity-60",
}) {
    const [busy, setBusy] = useState(false);
    const download = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const res = await api.get(endpoint, {
                params, responseType: "arraybuffer",
                headers: { Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
            });
            const cd = res.headers?.["content-disposition"] || "";
            const match = /filename="?([^"]+)"?/.exec(cd);
            const blob = new Blob([res.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = match ? match[1] : (filename || "MiseOne_Report.xlsx");
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(i18n.t("download.excelFailed", "Could not download Excel"));
        } finally {
            setBusy(false);
        }
    };
    return (
        <button
            type="button"
            data-testid="download-xlsx-btn"
            onClick={download}
            disabled={busy}
            className={className}
        >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            {label}
        </button>
    );
}
